import React, { useState } from 'react';
import { api } from '../api/af-social-tracker-api';
import { formatTimestampToLocaleString } from '../utils/time';

export interface TweetAuthor {
  id?: string;
  name?: string;
  userName?: string;
  profilePicture?: string;
  followers?: number;
  label?: string;
}

export interface Tweet {
  id: string;
  text: string;
  createdAt: string;
  author?: TweetAuthor;
  // 트윗 통계 정보 (선택적)
  viewCount?: number;
  likeCount?: number;
  bookmarkCount?: number;
  retweetCount?: number;
  quoteCount?: number;
  replyCount?: number;
}

interface TweetCardProps {
  tweet: Tweet;
}

const TweetCard: React.FC<TweetCardProps> = ({ tweet }) => {
  const [isBlacklisting, setIsBlacklisting] = useState(false);
  
  const handleBlacklist = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!tweet.author?.userName) return;
    
    if (window.confirm(`@${tweet.author.userName}을(를) 블랙리스트에 추가하시겠습니까?`)) {
      setIsBlacklisting(true);
      try {
        await api.blacklistAuthor(tweet.author.userName);
        alert('블랙리스트에 추가되었습니다.');
        // 성공 시 카드 숨기기 (선택사항)
        // 또는 부모 컴포넌트에서 리스트에서 제거하도록 처리
      } catch (error: any) {
        alert(error.message || '블랙리스트 추가에 실패했습니다.');
        console.error('블랙리스트 추가 실패:', error);
      } finally {
        setIsBlacklisting(false);
      }
    }
  };

  const formatRelativeTimeKo = (createdAtIso: string): string => {
    const createdMs = new Date(createdAtIso).getTime();
    const nowMs = Date.now();
    const diffMs = Math.max(0, nowMs - createdMs);

    const minuteMs = 60 * 1000;
    const hourMs = 60 * minuteMs;
    const dayMs = 24 * hourMs;

    if (diffMs < minuteMs) return 'Just now';
    if (diffMs < hourMs) return `${Math.floor(diffMs / minuteMs)} min ago`;
    if (diffMs < dayMs) return `${Math.floor(diffMs / hourMs)} hours ago`;
    return `${Math.floor(diffMs / dayMs)} days ago`;
  };
  // URL, 멘션, 해시태그, 달러태그를 감지하고 링크로 변환하는 함수
  const parseTextWithLinks = (text: string) => {
    // URL 패턴 정규식 (http, https, www로 시작하는 링크)
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    // 멘션 패턴 정규식 (@로 시작하는 사용자명)
    const mentionRegex = /@([a-zA-Z0-9_]+)/g;
    // 해시태그 패턴 정규식 (#로 시작하는 태그)
    const hashtagRegex = /#([a-zA-Z0-9_]+)/g;
    // 달러태그 패턴 정규식 ($로 시작하는 태그)
    const dollarTagRegex = /\$([a-zA-Z0-9_]+)/g;
    
    // 모든 패턴을 포함한 정규식으로 분할
    const combinedRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|@[a-zA-Z0-9_]+|#[a-zA-Z0-9_]+|\$[a-zA-Z0-9_]+)/g;
    const parts = text.split(combinedRegex);
    
    return parts.map((part, index) => {
      // URL 처리
      if (urlRegex.test(part)) {
        const href = part.startsWith('www.') ? `https://${part}` : part;
        
        return (
          <a
            key={index}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 underline transition-colors duration-200"
            title="외부 링크 열기"
          >
            {part}
          </a>
        );
      }
      
      // 멘션 처리
      if (mentionRegex.test(part)) {
        const username = part.substring(1); // @ 제거
        
        return (
          <a
            key={index}
            href={`https://twitter.com/${username}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 transition-colors duration-200"
            title={`@${username}의 트위터 프로필 보기`}
          >
            {part}
          </a>
        );
      }
      
      // 해시태그 처리
      if (hashtagRegex.test(part)) {
        const hashtag = part.substring(1); // # 제거
        
        return (
          <a
            key={index}
            href={`https://twitter.com/hashtag/${hashtag}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-400 hover:text-green-300 transition-colors duration-200"
            title={`#${hashtag} 해시태그 검색`}
          >
            {part}
          </a>
        );
      }
      
      // 달러태그 처리
      if (dollarTagRegex.test(part)) {
        const dollarTag = part.substring(1); // $ 제거
        
        return (
          <a
            key={index}
            href={`https://twitter.com/search?q=%24${dollarTag}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-yellow-400 hover:text-yellow-300 transition-colors duration-200"
            title={`$${dollarTag} 달러태그 검색`}
          >
            {part}
          </a>
        );
      }
      
      return part;
    });
  };

  const hasLabel = tweet.author?.label;
  const hasHighFollowers = tweet.author?.followers && tweet.author.followers >= 5000;
  
  return (
    <div className={`border rounded-xl p-5 hover:bg-gray-750 transition-all duration-200 hover:border-gray-600 shadow-lg ${
      hasLabel 
        ? 'bg-yellow-900/20 border-yellow-600/50' 
        : 'bg-gray-800 border-gray-700'
    }`}>
      {/* 헤더 */}
      <div className="group relative flex items-center space-x-3 mb-4">
        <div className="flex-shrink-0">
          <div 
            className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg" 
            style={{
              backgroundImage: `url(${tweet.author?.profilePicture})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between space-x-2 mb-1">
            <div className="flex items-center space-x-2 w-full justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <span className={`font-bold text-base ${
                    hasLabel 
                      ? 'text-yellow-300' 
                      : hasHighFollowers 
                        ? 'text-green-400' 
                        : 'text-gray-100'
                  }`} style={{
                    lineHeight: 1.2
                  }}>
                    {tweet.author?.name || 'Unknown'}
                  </span>
                  {hasLabel && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-900/50 text-yellow-300 border border-yellow-600/50">
                      {hasLabel}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400 text-sm">
                    @{tweet.author?.userName || 'unknown'}
                  </span>
                  {tweet.author?.followers !== undefined && (
                    <span className={`text-sm font-bold ${hasHighFollowers 
                      ? 'text-green-400' 
                      : 'text-gray-400'}`}>
                      {tweet.author.followers.toLocaleString()} 팔로워
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {tweet.author?.userName && (
                <button
                  onClick={handleBlacklist}
                  disabled={isBlacklisting}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 px-2 py-1 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:opacity-50 text-white text-xs font-medium rounded-md shadow-lg z-10"
                  title="블랙리스트에 추가"
                >
                  {isBlacklisting ? '처리 중...' : 'Blacklist'}
                </button>
              )}
              <a
                href={`https://twitter.com/${tweet.author?.userName || 'unknown'}/status/${tweet.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-8 h-8 bg-gray-700 hover:bg-gray-600 rounded-full transition-colors duration-200"
                title="트윗 원본 보기"
              >
                <svg 
                  className="w-4 h-4 text-gray-400 hover:text-white transition-colors duration-200" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
                  />
                </svg>
              </a>
            </div>
          </div>
          <div className="text-gray-500 text-xs">
            <span className="text-gray-400 mr-1">{formatRelativeTimeKo(tweet.createdAt)}</span>
            <span>
              {formatTimestampToLocaleString(tweet.createdAt, 'en-US', {
                timeZone: 'Asia/Seoul',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        </div>
      </div>
      
      
      {/* 트윗 내용 */}
      <div className="text-gray-200 text-sm leading-relaxed mb-4 whitespace-pre-wrap">
        {parseTextWithLinks(tweet.text)}
      </div>

      {/* 트윗 통계 정보 */}
      {(tweet.viewCount || tweet.likeCount || tweet.retweetCount || tweet.quoteCount || tweet.replyCount) && (
        <div className="border-t border-gray-700 pt-4">
          <div className="flex items-center justify-between text-sm text-gray-400">
            <div className="flex items-center space-x-4 text-xs">
              {/* 조회수 */}
              {!!Number(tweet.viewCount) && (
                <div className="flex items-center space-x-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span>{Number(tweet.viewCount).toLocaleString()}</span>
                </div>
              )}

              {/* 좋아요 */}
              {!!Number(tweet.likeCount) && (
                <div className="flex items-center space-x-1">
                  <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                  <span>{Number(tweet.likeCount).toLocaleString()}</span>
                </div>
              )}

              {/* 리트윗 */}
              {!!Number(tweet.retweetCount) && (
                <div className="flex items-center space-x-1">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.77 15.67c-.292-.293-.767-.293-1.06 0l-2.22 2.22V7.65c0-2.068-1.683-3.75-3.75-3.75h-5.85c-.414 0-.75.336-.75.75s.336.75.75.75h5.85c1.24 0 2.25 1.01 2.25 2.25v10.24l-2.22-2.22c-.293-.293-.768-.293-1.06 0s-.293.768 0 1.06l3.5 3.5c.146.147.338.22.53.22s.384-.073.53-.22l3.5-3.5c.293-.292.293-.767 0-1.06zM8.25 21c-1.24 0-2.25-1.01-2.25-2.25V8.51l2.22 2.22c.146.147.338.22.53.22s.384-.073.53-.22c.293-.293.293-.768 0-1.06l-3.5-3.5c-.293-.293-.768-.293-1.06 0l-3.5 3.5c-.293.292-.293.767 0 1.06s.767.293 1.06 0l2.22-2.22V18.75c0 2.068 1.683 3.75 3.75 3.75h5.85c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-5.85z"/>
                  </svg>
                  <span>{Number(tweet.retweetCount).toLocaleString()}</span>
                </div>
              )}

              {/* 답글 */}
              {!!Number(tweet.replyCount) && (
                <div className="flex items-center space-x-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>{Number(tweet.replyCount).toLocaleString()}</span>
                </div>
              )}

               {/* 북마크 */}
               {!!Number(tweet.bookmarkCount) && (
                 <div className="flex items-center space-x-1">
                   <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                     <path d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
                   </svg>
                   <span>{Number(tweet.bookmarkCount).toLocaleString()}</span>
                 </div>
               )}

              {/* Quote */}
              {!!Number(tweet.quoteCount) && (
                <div className="flex items-center space-x-1">
                  <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 9.017-9.57 1.103 0 2.25.194 3.25.54v3.5c-1.25-.5-2.5-.75-3.75-.75-2.5 0-4.5 2-4.5 4.5 0 1.5.75 2.75 1.75 3.5l-1.25 4.5h6.5zM6.017 21v-7.391c0-5.704 3.731-9.57 9.017-9.57 1.103 0 2.25.194 3.25.54v3.5c-1.25-.5-2.5-.75-3.75-.75-2.5 0-4.5 2-4.5 4.5 0 1.5.75 2.75 1.75 3.5l-1.25 4.5h6.5z"/>
                  </svg>
                  <span>{Number(tweet.quoteCount).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TweetCard;
